/**
 * Block tolerance summary + editor — shared by inspection results views.
 */
import React, { useCallback, useEffect, useState } from 'react';
import API from '../services/api';

/* Standard tiers always offered in the editor (rows are added for any other
   key extracted from the drawing — metric formats, DRAFT, ALL_HOLE, …). */
const BT_STANDARD = [
  { key: 'X',      label: 'X  (0–1 decimal place)',   placeholder: 'e.g. 0.5'    },
  { key: 'XX',     label: 'XX  (2 decimal places)',    placeholder: 'e.g. 0.01'   },
  { key: 'XXX',    label: 'XXX  (3 decimal places)',   placeholder: 'e.g. 0.005'  },
  { key: 'XXXX',   label: 'XXXX  (4 decimal places)',  placeholder: 'e.g. 0.0005' },
  { key: 'ANGLES', label: 'Angles  (± degrees °)',     placeholder: 'e.g. 1'      },
];

/* Friendly labels for keys commonly extracted from drawings. Anything not
   listed still gets its own row, labelled by its key. */
const BT_KNOWN_LABELS = {
  'X.X':    'X.X  (1 decimal place)',
  'X.XX':   'X.XX  (2 decimal places)',
  'X.XXX':  'X.XXX  (3 decimal places)',
  'X.XXXX': 'X.XXXX  (4 decimal places)',
  ANGLES:   'Angles  (± degrees °)',
  ANGLE:    'Angles  (± degrees °)',
  ANGULAR:  'Angles  (± degrees °)',
  DRAFT:    'Draft angles  (± degrees °)',
  ALL_HOLE: 'All holes  (±)',
  HOLES:    'All holes  (±)',
  FRACTIONS: 'Fractions  (±)',
};

// Internal / non-numeric keys that never appear as editable rows.
const _BT_INTERNAL = new Set(['_note', 'comment', 'BASIC']);

function _btRows(blockTolerances) {
  const rows = [];
  const seen = new Set();
  // 1. Every key actually extracted from the drawing, in its original order.
  if (blockTolerances && typeof blockTolerances === 'object') {
    for (const [k, v] of Object.entries(blockTolerances)) {
      if (String(k).startsWith('_') || _BT_INTERNAL.has(k)) continue;
      if (v == null || v === '' || isNaN(parseFloat(v))) continue;
      const std = BT_STANDARD.find(t => t.key === k);
      rows.push({
        key: k,
        label: std?.label || BT_KNOWN_LABELS[k] || `${k}  (±)`,
        placeholder: std?.placeholder || 'e.g. 0.1',
      });
      seen.add(k);
    }
  }
  // 2. Standard tiers not on the drawing, so users can add them.
  for (const t of BT_STANDARD) {
    if (!seen.has(t.key)) rows.push(t);
  }
  return rows;
}

function BlockTolerancesPanel({ blockTolerances, fileId, onSaved }) {
  const rows = _btRows(blockTolerances);

  // Pre-fill every row with the value extracted from the drawing, so an edit
  // to one tier never loses the others — unedited rows save back unchanged.
  const buildInitial = useCallback(() => _btRows(blockTolerances).reduce((acc, t) => {
    const raw = blockTolerances && blockTolerances[t.key];
    acc[t.key] = raw != null && raw !== '' ? String(raw) : '';
    return acc;
  }, {}), [blockTolerances]);

  const [values, setValues]   = useState(() => buildInitial());
  const [saving, setSaving]   = useState(false);
  const [saved,  setSaved]    = useState(false);
  const [error,  setError]    = useState(null);

  useEffect(() => {
    setValues(buildInitial());
    setSaved(false);
    setError(null);
  }, [fileId, buildInitial]);

  const hasExtracted = blockTolerances && Object.keys(blockTolerances).length > 0;

  const handleSave = async () => {
    setSaving(true); setError(null); setSaved(false);
    // Save every row that has a value (pre-filled ones included) — editing one
    // tier leaves the rest exactly as extracted. Cleared fields remove that key.
    const payload = {};
    Object.entries(values).forEach(([key, raw]) => {
      const v = parseFloat(raw);
      if (!isNaN(v)) payload[key] = v;
    });
    try {
      await API.updateBlockTolerances(fileId, payload);
      setSaved(true);
      if (onSaved) onSaved();
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e?.error || 'Failed to save block tolerances');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '12px 0 8px', maxWidth: 560, borderTop: '1px solid #e2e8e0' }}>
      <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: 8, color: '#334155' }}>
        Edit block tolerances
      </div>
      {!hasExtracted && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, background: '#FEF9E7',
          border: '1px solid #F7DC6F', fontSize: '0.82rem', color: '#7D6608', marginBottom: 12,
        }}>
          No block tolerance table was detected on this drawing. Enter values below to apply them to dimensions that use block tolerance (and to promote untoleranced numeric rows).
        </div>
      )}
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#F1F5F9' }}>
            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Format</th>
            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>On drawing</th>
            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>± (edit)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ key, label, placeholder }) => {
            const stored = blockTolerances && blockTolerances[key];
            const storedDisp = stored != null && stored !== '' ? String(stored) : '\u2014';
            return (
              <tr key={key} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '8px 10px', color: '#475569', fontFamily: 'system-ui' }}>{label}</td>
                <td style={{ padding: '8px 10px', color: '#64748b', fontFamily: 'monospace' }}>{storedDisp}</td>
                <td style={{ padding: '6px 10px' }}>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={values[key]}
                    placeholder={placeholder}
                    onChange={e => setValues(prev => ({ ...prev, [key]: e.target.value }))}
                    style={{
                      width: 120, padding: '5px 8px', borderRadius: 5,
                      border: '1px solid #CBD5E1', fontSize: '0.85rem',
                    }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '7px 16px', borderRadius: 7, border: 'none',
            background: '#D97706', color: '#FFF', fontWeight: 600,
            fontSize: '0.82rem', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Saving…' : 'Save & Apply'}
        </button>
        {saved && <span style={{ color: '#16A34A', fontSize: '0.8rem' }}>Saved — limits updated for block-tolerance rows.</span>}
        {error && <span style={{ color: '#DC2626', fontSize: '0.8rem' }}>{error}</span>}
      </div>
      <div style={{ marginTop: 8, fontSize: '0.76rem', color: '#94a3b8' }}>
        Fields are pre-filled with the values extracted from the drawing — change only what you need;
        unedited tiers are saved back unchanged. Clearing a field removes that tier; clear all fields
        and save to remove block tolerances entirely (affected dimensions revert to “—” / BASIC).
      </div>
    </div>
  );
}

// Keys that are internal / non-dimensional and must not appear in the summary.
const _SKIP_DISPLAY_KEYS = new Set(['_note', 'comment', 'BASIC']);

export function formatBlockTolerancesSummary(blockTolerances) {
  if (!blockTolerances || typeof blockTolerances !== 'object') {
    return '\u2014  (none on drawing)';
  }
  // Show every key from the actual dict — covers both inch (X/XX/XXX/XXXX) and
  // metric (X.X/X.XX/X.XXX) formats plus ANGLES, DRAFT, ALL_HOLE, etc.
  const entries = Object.entries(blockTolerances).filter(
    ([k, v]) =>
      !String(k).startsWith('_') &&
      !_SKIP_DISPLAY_KEYS.has(k) &&
      v != null &&
      v !== ''
  );
  if (entries.length === 0) return '\u2014  (none on drawing)';
  return entries.map(([k, v]) => `${k}: \u00B1${v}`).join('  \u00B7  ');
}

/**
 * @param {{ blockTolerances: object, fileId?: number|string, onSaved?: () => void }} props
 */
export default function BlockTolerancesSection({ blockTolerances, fileId, onSaved }) {
  const [panelOpen, setPanelOpen] = useState(false);
  const canEdit = fileId != null && fileId !== '';

  return (
    <div style={{ marginTop: 10, marginBottom: panelOpen ? 4 : 10 }}>
      <div style={{
        padding: '8px 12px',
        background: '#f8fafc',
        borderRadius: 8,
        border: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 8,
      }}>
        <div style={{ minWidth: 0, flex: '1 1 200px' }}>
          <span style={{
            fontSize: '0.68rem', fontWeight: 700, color: '#64748b',
            letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>Block tolerance</span>
          <div style={{
            marginTop: 4, fontSize: '0.8rem', fontFamily: 'ui-monospace, monospace',
            color: '#334155', lineHeight: 1.4, wordBreak: 'break-word',
          }}>
            {formatBlockTolerancesSummary(blockTolerances)}
          </div>
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={() => setPanelOpen(o => !o)}
            style={{
              flexShrink: 0,
              padding: '5px 12px',
              borderRadius: 6,
              border: '1px solid #cbd5e1',
              background: panelOpen ? '#e2e8f0' : '#fff',
              color: '#475569',
              fontSize: '0.78rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {panelOpen ? 'Hide editor' : 'Edit block tolerance'}
          </button>
        )}
      </div>
      {canEdit && panelOpen && (
        <BlockTolerancesPanel
          blockTolerances={blockTolerances}
          fileId={fileId}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}
